import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { askApi } from '@/lib/ask';

/**
 * A clickable citation chip. Resolves a chunk id to its source document and
 * navigates to the document viewer — so every cited chunk (in Ask, Compose,
 * Brief, Audit, Compare, AI Results, Workflows) is a launch point back to the
 * source instead of a dead number.
 *
 * `inline` matches the in-prose look used inside Ask answers; the default
 * badge look is for result footers.
 */
export function CitationChip({
  wsId,
  chunkId,
  label,
  inline,
}: {
  wsId: string;
  chunkId: string | number;
  /** Short human citation number to display (e.g. 1, 2). Falls back to the id. */
  label?: string | number;
  inline?: boolean;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const id = String(chunkId);

  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const detail = await askApi.getChunk(wsId, id);
      if (!('error' in detail)) {
        navigate(`/app/workspace/${wsId}/docs/${detail.documentId}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      title={t('citation.view_source', { defaultValue: 'View source document' })}
      className={
        inline
          ? 'mx-0.5 chip-citation text-[10px] num align-baseline hover:bg-citation-500/15 transition disabled:opacity-50'
          : 'inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100 transition num disabled:opacity-50'
      }
    >
      {label != null ? (inline ? label : `#${label}`) : inline ? `chunk:${id}` : `#${id}`}
    </button>
  );
}
